import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import WeeklySlotPicker from '../commerce/WeeklySlotPicker';
import { standingApi, StandingPick, WEEKDAYS, dayMonth, time12, slotText } from '../commerce/standingApi';

/* A member's weekly standing slots (member modal → Trainers). Staff see each
   slot's coming weeks, act on flagged weeks (trainer on leave, a clash, the
   quota used — never moved silently), skip or restore a week, move a slot,
   end it, and set the times for a plan sold without them ("gym assigns", or
   a desk sale finished later). */

type OccState = 'booked' | 'held' | 'skipped' | 'paused' | 'cancelled' | 'attended' | 'missed';
interface Occurrence { date: string; state: OccState; bookingId: string | null; skipReason: string | null; issue: { id: string; kind: string; detail: string | null } | null }
interface Series {
  id: string; entitlementId: string; offeringName: string;
  trainer: { id: string; name: string }; className: string; branch: { id: string; name: string };
  weekday: number; startTime: string; durationMin: number; startsOn: string; endsOn: string | null; openIssues: number;
  upcoming: Occurrence[];
}
interface Awaiting { entitlementId: string; offeringName: string; trainerId: string | null; scheduling: string; slots: { min: number; max: number }; validTo: string | null }
interface Data { today: string; series: Series[]; awaiting: Awaiting[]; openIssues: number }

const STATE: Record<OccState, { label: string; cls: string }> = {
  booked: { label: 'Booked', cls: 'bg-lime-100 border-lime-300 text-lime-900' },
  held: { label: 'Held', cls: 'bg-white border-slate-200 text-slate-700' },
  skipped: { label: 'Skipped', cls: 'bg-slate-50 border-slate-200 text-slate-400 line-through' },
  paused: { label: 'Paused', cls: 'bg-sky-50 border-sky-200 text-sky-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-50 border-slate-200 text-slate-400 line-through' },
  attended: { label: 'Done', cls: 'bg-lime-50 border-lime-200 text-lime-800' },
  missed: { label: 'Missed', cls: 'bg-rose-50 border-rose-200 text-rose-700' },
};
const ISSUE: Record<string, string> = { leave: 'Trainer on leave', conflict: 'Clash', quota: 'No sessions left' };

export default function StandingSlotsPanel({ gymId, memberId, readOnly }: { gymId: string; memberId: string; readOnly: boolean }) {
  const [data, setData] = useState<Data | null>(null);
  const [week, setWeek] = useState<{ s: Series; o: Occurrence } | null>(null);
  const [picker, setPicker] = useState<{ mode: 'add'; a: Awaiting } | { mode: 'move'; s: Series } | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [moveTime, setMoveTime] = useState('');

  const load = useCallback(() => {
    standingApi<Data>(`/member?gymId=${gymId}&memberId=${memberId}`).then(setData).catch(() => setData({ today: '', series: [], awaiting: [], openIssues: 0 }));
  }, [gymId, memberId]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<any>, ok: string) => {
    setSaving(true);
    try {
      await fn();
      toast.success(ok);
      setWeek(null);
      setPicker(null);
      setPickerError(null);
      load();
    } catch (e: any) {
      if (picker) setPickerError(e.message);
      else toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data || (data.series.length === 0 && data.awaiting.length === 0)) return null;

  const save = (pick: StandingPick) => {
    if (!picker) return;
    if (picker.mode === 'add') {
      run(() => standingApi(`/entitlements/${picker.a.entitlementId}/series`, { method: 'POST', body: pick }), 'Weekly times set');
    } else {
      const t = pick.times[0];
      run(() => standingApi(`/series/${picker.s.id}/move`, { method: 'POST', body: { ...t, acceptClashes: pick.acceptClashes } }), `Now every ${slotText(t)}`);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Weekly slots</p>
        {data.openIssues > 0 && <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">{data.openIssues} to sort out</span>}
      </div>

      {data.awaiting.map(a => (
        <div key={a.entitlementId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 p-2.5">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{a.offeringName}</p>
            <p className="text-[11px] text-amber-800">{a.trainerId ? 'No weekly times yet' : 'Assign a coach above, then set the weekly times'}</p>
          </div>
          {!readOnly && a.trainerId && (
            <button onClick={() => { setPickerError(null); setPicker({ mode: 'add', a }); }} className="h-9 px-3 rounded-lg bg-indigo-600 text-white text-xs font-black">Set weekly times</button>
          )}
        </div>
      ))}

      {data.series.map(s => (
        <div key={s.id} className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">Every {WEEKDAYS[s.weekday]} · {time12(s.startTime)} <span className="font-medium text-gray-500">· {s.durationMin} min</span></p>
              <p className="text-[11px] text-gray-500 truncate">{s.trainer.name} · {s.className} · {s.branch.name} · {s.offeringName}{s.endsOn ? ` · until ${dayMonth(s.endsOn)}` : ''}</p>
            </div>
            {!readOnly && (
              <div className="flex gap-1.5">
                <button onClick={() => { setPickerError(null); setPicker({ mode: 'move', s }); }} className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-700 hover:border-slate-400">Move</button>
                <button onClick={() => {
                  if (window.confirm(`End ${s.trainer.name}'s ${WEEKDAYS[s.weekday]} ${time12(s.startTime)} slot from tomorrow? Booked sessions after today are cancelled and go back on the plan.`)) {
                    run(() => standingApi(`/series/${s.id}/end`, { method: 'POST', body: {} }), 'Weekly slot ended');
                  }
                }} className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-rose-600 hover:border-rose-300">End</button>
              </div>
            )}
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {s.upcoming.map(o => (
              <button key={o.date} disabled={readOnly} onClick={() => { setMoveTime(s.startTime); setWeek({ s, o }); }}
                className={`shrink-0 min-w-[56px] min-h-[48px] rounded-lg border px-1.5 py-1 text-center transition-transform active:scale-95 ${STATE[o.state].cls} ${o.issue ? 'ring-2 ring-rose-400' : ''}`}
                title={o.issue ? `${ISSUE[o.issue.kind] ?? o.issue.kind}: ${o.issue.detail ?? ''}` : STATE[o.state].label}>
                <div className="text-[11px] font-black">{dayMonth(o.date)}</div>
                <div className="text-[9px] font-bold uppercase tracking-wide">{o.issue ? ISSUE[o.issue.kind] ?? 'Issue' : STATE[o.state].label}</div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {week && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 p-4" onClick={e => { e.stopPropagation(); setWeek(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-black text-slate-800">{WEEKDAYS[week.s.weekday]} {dayMonth(week.o.date)} · {time12(week.s.startTime)}</h3>
              <p className="text-xs text-slate-500">{week.s.trainer.name} · {STATE[week.o.state].label}</p>
            </div>
            {week.o.issue && (
              <div className="p-3 rounded-lg bg-rose-50 text-rose-800 text-sm">
                <b>{ISSUE[week.o.issue.kind] ?? 'Issue'}.</b> {week.o.issue.detail}. Nothing has been moved — choose what happens.
              </div>
            )}
            {week.o.issue ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="time" value={moveTime} step={900} onChange={e => setMoveTime(e.target.value)} className="h-10 px-2 border border-slate-200 rounded-lg text-sm" />
                  <button disabled={saving || !moveTime} onClick={() => run(() => standingApi(`/issues/${week.o.issue!.id}/resolve`, { method: 'POST', body: { action: 'move', startTime: moveTime } }), 'Moved for that week')}
                    className="flex-1 h-10 rounded-lg bg-indigo-600 text-white text-sm font-black disabled:opacity-40">Move this week</button>
                </div>
                <div className="flex gap-2">
                  <button disabled={saving} onClick={() => run(() => standingApi(`/issues/${week.o.issue!.id}/resolve`, { method: 'POST', body: { action: 'skip' } }), 'Week skipped')}
                    className="flex-1 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-700">Skip this week</button>
                  <button disabled={saving} onClick={() => run(() => standingApi(`/issues/${week.o.issue!.id}/resolve`, { method: 'POST', body: { action: 'dismiss' } }), 'Dismissed')}
                    className="flex-1 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-500">Dismiss</button>
                </div>
              </div>
            ) : week.o.state === 'skipped' ? (
              <button disabled={saving} onClick={() => run(() => standingApi(`/series/${week.s.id}/restore`, { method: 'POST', body: { date: week.o.date } }), 'Week restored')}
                className="w-full h-10 rounded-lg bg-indigo-600 text-white text-sm font-black">Restore this week</button>
            ) : ['booked', 'held'].includes(week.o.state) ? (
              <button disabled={saving} onClick={() => run(() => standingApi(`/series/${week.s.id}/skip`, { method: 'POST', body: { date: week.o.date } }), 'Week skipped')}
                className="w-full h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-700">Skip this week (the session goes back on the plan)</button>
            ) : <p className="text-sm text-slate-500">Nothing to do for this week.</p>}
          </div>
        </div>
      )}

      {picker && (
        <WeeklySlotPicker
          title={picker.mode === 'add' ? `Weekly times — ${picker.a.offeringName}` : `Move ${WEEKDAYS[picker.s.weekday]} ${time12(picker.s.startTime)}`}
          load={branchId => {
            const qs = new URLSearchParams();
            if (branchId) qs.set('branchId', branchId);
            if (picker.mode === 'move') qs.set('seriesId', picker.s.id);
            const ent = picker.mode === 'add' ? picker.a.entitlementId : picker.s.entitlementId;
            return standingApi(`/entitlements/${ent}/grid?${qs.toString()}`);
          }}
          count={picker.mode === 'move' ? { min: 1, max: 1 } : undefined}
          saving={saving}
          error={pickerError}
          onClose={() => setPicker(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

